/**
 * @file DatePickerComponent.jsx
 * @description A reusable date picker component that renders a MUI TextField
 *              triggering a portal-based DayPicker calendar on click.
 *              Supports controlled date input, outside-click dismissal,
 *              and repositioning on scroll/resize.
 *
 * @component DatePickerComponent
 * @param {string}   label          - TextField label.
 * @param {Date}     date           - Currently selected date value.
 * @param {Function} onDateChange   - Callback fired when a date is selected.
 * @param {number}   [marginRight=20] - Right margin applied to the wrapper (px).
 * @param {boolean}  [fullWidth=false] - Whether the input should take full width.
 *
 * @author Carafin
 * @created 2026-04-09
 */
import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import styled from "styled-components";
import TextField from "@mui/material/TextField";

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
  margin: 0px 0;
`;

const CalendarContainer = styled.div`
  background: white;
  padding: 4px;
  border: 1px solid #ccc;
  width: max-content;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
`;

function DatePickerComponent({
  label,
  date,
  onDateChange,
  marginRight = 20,
  fullWidth = false,
}) {
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const portalRef = useRef(null);
  const [portalPos, setPortalPos] = useState({ top: 0, left: 0, width: 0 });

  const handleDateChange = (selectedDate) => {
    onDateChange(selectedDate);
    setIsCalendarVisible(false);
  };

  const handleTextboxClick = () => {
    setIsCalendarVisible((prev) => !prev);
  };

  const parseDate = (val) => {
    if (!val) { return null; }
    if (val instanceof Date && !isNaN(val)) { return val; }
    // Try to parse string dates
    const d = new Date(val);
    return isNaN(d) ? null : d;
  };

  const formatDate = (val) => {
    let d = val;
    if (!(d instanceof Date) || isNaN(d)) {
       d = parseDate(val);
    }
    if (!d || isNaN(d)) { return ""; }

    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const updatePortalPosition = () => {
    const el = inputRef.current;
    if (!el) { return; }
    const rect = el.getBoundingClientRect();
    // use viewport coordinates for fixed positioning
    setPortalPos({ top: rect.bottom, left: rect.left, width: rect.width });
  };

  useEffect(() => {
    if (!isCalendarVisible) return;
    updatePortalPosition();
    const onDocClick = (e) => {
      const insideWrapper =
        wrapperRef.current && wrapperRef.current.contains(e.target);
      const insidePortal =
        portalRef.current && portalRef.current.contains(e.target);
      if (!insideWrapper && !insidePortal) {
        setIsCalendarVisible(false);
      }
    };
    const onResizeScroll = () => updatePortalPosition();
    document.addEventListener("click", onDocClick);
    window.addEventListener("resize", onResizeScroll);
    window.addEventListener("scroll", onResizeScroll, true);
    return () => {
      document.removeEventListener("click", onDocClick);
      window.removeEventListener("resize", onResizeScroll);
      window.removeEventListener("scroll", onResizeScroll, true);
    };
  }, [isCalendarVisible]);

  return (
    <Wrapper
      ref={wrapperRef}
      style={{
        marginRight: `${marginRight}px`,
        width: fullWidth ? "100%" : "auto",
      }}
    >
      <TextField
        inputRef={inputRef}
        label={label}
        value={formatDate(date)}
        onClick={handleTextboxClick}
        size="small"
        fullWidth={fullWidth}
        InputProps={{ readOnly: true }}
      />
      {isCalendarVisible &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={portalRef}
            style={{
              position: "fixed",
              top: portalPos.top + "px",
              left: portalPos.left + "px",
              zIndex: 2147483647,
              width: portalPos.width,
            }}
          >
            <CalendarContainer>
              <DayPicker selected={date} onDayClick={handleDateChange} />
            </CalendarContainer>
          </div>,
          document.body
        )}
    </Wrapper>
  );
}

export default DatePickerComponent;
